/* eslint-disable react/no-array-index-key */
// @flow
import React from "react";
import { withRouter } from "react-router-dom";

import { FlashMessageConsumer, NewInquiryPage } from "buba-components";
import DataFetcher from "buba-components/src/components/DataFetcher";
import { denormalizeJsonApi } from "buba-components/src/utils/denormalizeJsonApi";
import { PageHeader, Accordion, AccordionElement } from "buba-components-ts";
import paths from "../../data/paths";
import { bankInquiryTabsNew } from "../../data/navLinks";
import { getInquiryFidesDataV2 } from "../../api/v2actions";
import GeneralData from "./GeneralData";

import styles from "./BankInquiryApplication.scss";
import ErpFgnDetails from "./ErpFgnDetails";
import LoanGuaranteeDetails from "./LoanGuaranteeDetails";
import DownloadInquiryDocuments from "./DownloadInquiryDocuments";
import handleGuaranteeRequestPdfDownload from "../../api/handlers";

type BankInquiryApplicationProps = {
  match: {
    params: {
      id: string
    }
  }
};

const KFW_ERP_FGN_GUARANTEE_TYPE = "kfw_erp_fgn_guarantee";
const LOAN_GUARANTEE_TYPE = "loan_guarantee";

const fidesResponseTypeTitleMap = {
  [LOAN_GUARANTEE_TYPE]: "Bürgschaftsantrag",
  [KFW_ERP_FGN_GUARANTEE_TYPE]: "ERP-Förderkredit für Gründung und Nachfolge Antrag"
};

const groupFidesResponsesByType = fidesResponses => {
  return fidesResponses.reduce((acc, response) => {
    const { productType } = response;
    if (!acc[productType]) {
      acc[productType] = [];
    }
    acc[productType].push(response);
    return acc;
  }, {});
};

const BankInquiryApplication = ({
  match: {
    params: { id }
  }
}: BankInquiryApplicationProps) => {
  const handleDownload = () => handleGuaranteeRequestPdfDownload(id);

  return (
    <FlashMessageConsumer>
      {({ addMessage }) => (
        <DataFetcher
          initialPayload={{
            id
          }}
          mapper={denormalizeJsonApi}
          onError={msg => addMessage(msg, "failure")}
          apiAction={getInquiryFidesDataV2}
          redirectOnError
        >
          {({ data, isLoading }) => {
            const fidesResponses = data?.fidesMetadata?.responses || [];
            const firstFidesResponse = fidesResponses[0];
            const summaryPdfsAvailable = data?.fidesMetadata?.requestPdfsAvailable;

            if (!firstFidesResponse) {
              return null;
            }

            const groupedFidesResponses = groupFidesResponsesByType(fidesResponses);

            return (
              <NewInquiryPage
                backPath={paths.bankInquiriesList}
                tabs={inquiryId => bankInquiryTabsNew(inquiryId, data)}
                isLoadingData={isLoading}
                site="bank"
              >
                <div className={styles.pageContent}>
                  <PageHeader header="Antragsdetails" isBoldHeader />
                  {summaryPdfsAvailable && (
                    <DownloadInquiryDocuments handleDownload={handleDownload} />
                  )}
                  <Accordion>
                    <AccordionElement title="Allgemeine Daten">
                      <GeneralData data={firstFidesResponse} />
                    </AccordionElement>
                    {Object.entries(groupedFidesResponses).map(([type, responses]) => {
                      let ProductDetailsComponent = null;
                      switch (type) {
                        case KFW_ERP_FGN_GUARANTEE_TYPE:
                          ProductDetailsComponent = ErpFgnDetails;
                          break;
                        case LOAN_GUARANTEE_TYPE:
                          ProductDetailsComponent = LoanGuaranteeDetails;
                          break;
                        default:
                          ProductDetailsComponent = () => null;
                      }

                      return responses.map((fidesResponse, index) => (
                        <AccordionElement
                          title={`Details ${fidesResponseTypeTitleMap[type]} #${index + 1}${fidesResponse.productId ? ` (${fidesResponse.productId})` : ""}`}
                          key={`${type}-${index}`}
                        >
                          <ProductDetailsComponent data={fidesResponse} />
                        </AccordionElement>
                      ));
                    })}
                  </Accordion>
                </div>
              </NewInquiryPage>
            );
          }}
        </DataFetcher>
      )}
    </FlashMessageConsumer>
  );
};

export default withRouter(BankInquiryApplication);
